/**
 * SmtpPanel — admin controls for outgoing email (SMTP) server.
 *
 * Reads/writes /api/admin/smtp-config (auth-protected) and offers a "Enviar
 * email de teste" action that hits /api/admin/smtp-config/test. Password is
 * write-only: the API returns `password_set` boolean; leaving the field blank
 * on save keeps the current password intact.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Save, Mail, ShieldCheck, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SECURITY_OPTIONS = [
  { value: "starttls", label: "STARTTLS (porta 587 — recomendado)" },
  { value: "ssl",      label: "SSL/TLS (porta 465)" },
  { value: "none",     label: "Sem segurança (não recomendado)" },
];

export function SmtpPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testChannel, setTestChannel] = useState("auto"); // auto | smtp | sendgrid
  const [showPw, setShowPw] = useState(false);
  const [showSg, setShowSg] = useState(false);

  const token = localStorage.getItem("admin_token") || sessionStorage.getItem("token") || localStorage.getItem("token") || "";
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/smtp-config`, { headers: authHeaders });
        setCfg({
          enabled: !!data.enabled,
          host: data.host || "",
          port: data.port || 587,
          security: data.security || "starttls",
          username: data.username || "",
          password: "",
          password_set: !!data.password_set,
          sender_email: data.sender_email || "",
          sender_name: data.sender_name || "SUPERTMATIK",
          sendgrid_api_key: "",
          sendgrid_key_set: !!data.sendgrid_key_set,
          sendgrid_region: data.sendgrid_region || "global",
          alert_email: data.alert_email || "",
        });
      } catch (err) {
        toast.error("Não foi possível carregar a configuração SMTP.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (field, value) => setCfg((s) => ({ ...s, [field]: value }));

  const onSave = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload.password_set;
      delete payload.sendgrid_key_set;
      if (!payload.password) delete payload.password; // preserve existing
      if (!payload.sendgrid_api_key) delete payload.sendgrid_api_key; // preserve existing
      const { data } = await axios.put(`${API}/api/admin/smtp-config`, payload, { headers: authHeaders });
      setCfg((s) => ({
        ...s,
        password: "",
        password_set: !!data.password_set,
        sendgrid_api_key: "",
        sendgrid_key_set: !!data.sendgrid_key_set,
      }));
      toast.success("Configuração de envio guardada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!testTo) {
      toast.error("Introduz um email de destino para o teste.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/smtp-config/test`,
        { to_email: testTo, channel: testChannel },
        { headers: authHeaders },
      );
      if (data?.ok) {
        toast.success(`Email de teste enviado para ${testTo} via ${data.via || testChannel}.`);
      } else {
        toast.error(data?.error || "Falha no envio do email de teste.", { duration: 14000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no envio do email de teste.");
    } finally {
      setTesting(false);
    }
  };

  if (loading || !cfg) {
    return <div className="text-sm text-slate-500 py-2">A carregar…</div>;
  }

  return (
    <div className="space-y-4" data-testid="smtp-panel">
      <p className="text-[11px] text-slate-500">
        Configura o teu servidor SMTP (domínio próprio) para envio das credenciais aos professores após inscrição
        e outros emails automáticos. A password é guardada de forma segura na base de dados e nunca é devolvida em
        leitura — deixa em branco para manter a atual.
      </p>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
          data-testid="smtp-enabled"
        />
        <span className="text-sm font-bold text-slate-700">Ativar envio via SMTP próprio</span>
        {cfg.password_set && (
          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            <ShieldCheck className="w-3 h-3" /> Password guardada
          </span>
        )}
      </label>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Servidor SMTP (host)</Label>
          <Input
            value={cfg.host}
            onChange={(e) => set("host", e.target.value)}
            placeholder="smtp.dominio.pt"
            data-testid="smtp-host"
          />
        </div>
        <div>
          <Label className="text-xs">Porta</Label>
          <Input
            type="number"
            value={cfg.port}
            onChange={(e) => set("port", Number(e.target.value) || 0)}
            data-testid="smtp-port"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Segurança</Label>
          <select
            value={cfg.security}
            onChange={(e) => set("security", e.target.value)}
            className="w-full h-9 text-sm rounded border border-slate-200 px-2 bg-white"
            data-testid="smtp-security"
          >
            {SECURITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Utilizador</Label>
          <Input
            value={cfg.username}
            onChange={(e) => set("username", e.target.value)}
            placeholder="ex: noreply@dominio.pt"
            data-testid="smtp-username"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="text-xs">Password</Label>
          <div className="relative">
            <Input
              type={showPw ? "text" : "password"}
              value={cfg.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder={cfg.password_set ? "•••••••• (deixar em branco = manter)" : ""}
              data-testid="smtp-password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-500 hover:text-slate-700"
              data-testid="smtp-password-toggle"
            >
              {showPw ? "Ocultar" : "Ver"}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Email do remetente</Label>
          <Input
            value={cfg.sender_email}
            onChange={(e) => set("sender_email", e.target.value)}
            placeholder="noreply@dominio.pt"
            data-testid="smtp-sender-email"
          />
        </div>
        <div>
          <Label className="text-xs">Nome do remetente</Label>
          <Input
            value={cfg.sender_name}
            onChange={(e) => set("sender_name", e.target.value)}
            placeholder="SUPERTMATIK"
            data-testid="smtp-sender-name"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
        <Button onClick={onSave} disabled={saving} data-testid="smtp-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar configuração
        </Button>
        <div className="h-6 w-px bg-slate-200 mx-1" />
        <select
          value={testChannel}
          onChange={(e) => setTestChannel(e.target.value)}
          className="h-9 text-sm rounded border border-slate-200 px-2 bg-white"
          data-testid="smtp-test-channel"
          title="Canal de envio a testar"
        >
          <option value="auto">Auto (SMTP → SendGrid)</option>
          <option value="smtp">Apenas SMTP</option>
          <option value="sendgrid">Apenas SendGrid</option>
        </select>
        <Input
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="destinatario@exemplo.com"
          className="w-64"
          data-testid="smtp-test-to"
        />
        <Button
          variant="outline"
          onClick={onTest}
          disabled={testing || (!cfg.enabled && !cfg.sendgrid_key_set)}
          data-testid="smtp-test"
        >
          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          Enviar email de teste
        </Button>
      </div>

      <div className="pt-3 mt-2 border-t border-slate-200">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <h4 className="text-sm font-bold text-slate-700">SendGrid (fallback HTTP)</h4>
          {cfg.sendgrid_key_set && (
            <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              <ShieldCheck className="w-3 h-3" /> API key guardada
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          Alternativa HTTP ao SMTP (usa HTTPS/443, imune a bloqueios de portas de mail).
          Envia-se automaticamente por SendGrid se o SMTP falhar. Cria uma conta grátis em{" "}
          <a href="https://sendgrid.com" target="_blank" rel="noreferrer" className="text-blue-600 underline">sendgrid.com</a>{" "}
          e cola a API key aqui.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">SendGrid API key</Label>
            <div className="relative">
              <Input
                type={showSg ? "text" : "password"}
                value={cfg.sendgrid_api_key}
                onChange={(e) => set("sendgrid_api_key", e.target.value)}
                placeholder={cfg.sendgrid_key_set ? "•••••••• (deixar em branco = manter)" : "SG.xxxxxxxx..."}
                data-testid="smtp-sendgrid-key"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowSg((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-500 hover:text-slate-700"
                data-testid="smtp-sendgrid-key-toggle"
              >
                {showSg ? "Ocultar" : "Ver"}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Região SendGrid</Label>
            <select
              value={cfg.sendgrid_region}
              onChange={(e) => set("sendgrid_region", e.target.value)}
              className="w-full h-9 text-sm rounded border border-slate-200 px-2 bg-white"
              data-testid="smtp-sendgrid-region"
            >
              <option value="global">Global (default)</option>
              <option value="eu">EU (data residency)</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Email para alertas do painel Métricas</Label>
            <Input
              type="email"
              value={cfg.alert_email || ""}
              onChange={(e) => set("alert_email", e.target.value)}
              placeholder="admin@dominio.pt (vazio = usa o remetente)"
              data-testid="smtp-alert-email"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Destinatário dos alertas críticos disparados pelo painel <b>Admin → Métricas</b> (quando o valor se mantém acima do threshold pelo tempo definido em <i>Email após (s)</i>).
            </p>
          </div>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
        <b>Ordem de envio:</b> SMTP próprio (se ativado) → SendGrid HTTP (se API key configurada) → modo MOCK (credenciais visíveis no backoffice).
      </div>
    </div>
  );
}
